import {useEffect, useRef, useState} from 'react';
import './App.css';
import {Snackbar, ThemeProvider} from './tmwx-design-web/src';
import {usePermissions} from 'tmwx-design-common';
import {createBrowserRouter, Navigate, RouterProvider, To} from "react-router-dom";
import SignIn from './screens/SignIn';
import {useDispatch, useSelector} from 'react-redux';
import Error404 from './tmwx-design-web/src/examples/Error404';
import ATRWrapper from './screens/atr/ATRWrapper';
import Loading from './screens/Loading';
import {default as PortalUsers} from './screens/portal/Users';
import Roles from './screens/portal/Roles';
import {default as ATRUsers} from './screens/atr/Users';
import {default as ATREvents} from './screens/atr/Events';
import {default as PortalEvents} from './screens/portal/Events';
import PortalWrapper from './screens/portal/PortalWrapper';
import PortalDashboard from './screens/portal/PortalDashboard';
import i18n from 'i18next';
import {useTranslation} from 'react-i18next';
import {setAuthToken, setDefaultBaseUrl, setupAxiosInterceptors} from './gui-common/axiosHelper';
import {isEmpty} from './gui-common/appHelper';
import constants from './gui-common/constants.json';
import {RootState} from './gui-common/redux/store';
import {RESPONSE_CODES, ResponseBase} from './gui-common/redux/data';
import {SnackbarState} from './gui-common/redux/slice/snackbarSlice';
import {setSnackbarData} from './gui-common/redux/action/snackbarAction';
import {authActions, AuthState} from './gui-common/redux/slice/portal/authSlice';
import {checkIn, getUserDetails} from './gui-common/redux/action/portal/authAction';
import {clearPreferencesResponse, updatePreferences} from './gui-common/redux/action/portal/userAction';
import Companies from './screens/atr/Companies';
import ATRDashboard from './screens/atr/ATRDashboard';
import {getNotificationsPoll} from './gui-common/redux/action/portal/notificationAction';
import Machines from "./screens/atr/Machines";
import MachineDetails from "./screens/atr/MachineDetails";
import Firmwares from './screens/atr/Firmwares';
import OperationalParams from './screens/atr/OperationalParams';
import Logs from "./screens/atr/Logs";
import Report from "./screens/atr/Report";
import Commands from './screens/atr/Commands';
import MachineCategories from './screens/atr/MachineCategories';
import MachineTypeScreen from "./screens/atr/MachineType";
import Locations from './screens/atr/Locations';
import EvaDts from "./screens/atr/EvaDts";
import Planograms from "./screens/atr/Planograms";
import EfeWrapper from "./screens/efe/EfeWrapper";
import EfeDashboard from "./screens/efe/EfeDashboard";
import MeasurementLocationCategories from "./screens/efe/MeasurementLocationCategories";
import MeasurementLocation from "./screens/efe/MeasurementLocation";
import EfeLogs from "./screens/efe/EfeLogs";
import EfeType from "./screens/efe/DeviceType";
import Providers from "./screens/efe/Providers";
import EfeFirmwares from "./screens/efe/EfeFirmwares";
import Metrics from "./screens/efe/Metrics";
import MeasurementData from "./screens/efe/MeasurementData";
import EfeParams from "./screens/efe/DeviceParams";
import DeviceCommands from "./screens/efe/DeviceCommands";
import EfeEvents from "./screens/efe/EfeEvents";
import EfeLocations from "./screens/efe/EfeLocations";
import EfeUsers from "./screens/efe/EfeUsers";
import MeasurementLocationDetails from "./screens/efe/MeasurementLocationDetails";
import MetricsScreen from "./screens/efe/Metrics";
import MqttMessages from "./screens/efe/MqttMessages";
import Device from "./screens/efe/Device";
import PasswordSetup from "./screens/PasswordSetup";
import {getActiveCompany, getATRCompanyList} from "gui-common/redux/action/efe/providerAction";
import {Company} from "gui-common/redux/data/company";
import MetricParams from "./screens/efe/MetricParams";
import PythonScript from "./screens/efe/PythonScript";
import Register from "./screens/efe/RegisterTable";
import RegisterTable from "./screens/efe/RegisterTable";
import TestRegisterTable from "./screens/efe/TestRegisterTable";

const getTheme = (): 'light' | 'dark' => {
  const theme: any = localStorage.getItem(constants.THEME)

  if (isEmpty(theme)) {
    localStorage.setItem(constants.THEME, "light")
    return 'light'
  } else {
    return theme
  }
}

function App() {
  const { t } = useTranslation()
  const dispatch: any = useDispatch();
  const [theme] = useState<'light' | 'dark'>(getTheme())
  const { setPermissions, hasModules, hasRights } = usePermissions()
  const auth = useSelector<RootState, AuthState>(state => state.auth)
  const preferencesResponse = useSelector<RootState, ResponseBase | null>(state => state.portalUser.preferencesResponse)
  const snackbar = useSelector<RootState, SnackbarState>(state => state.snackbar)
  const activeCompany = useSelector<RootState, Company>(state => state.auth.activeCompany);
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false)
  const notificationInterval = useRef<any>()
  const canEditProvider = hasRights(["efe_admin_all_efe"]);

  const onDismissSnackBar = () => {
    setShowSnackbar(false)
    dispatch(setSnackbarData(null))
  }

  const getDefaultRoute = (): To => {
    const pathName = window.location.pathname + window.location.search

    if (pathName.includes('/atr') || pathName.includes('/portal') || pathName.includes('/efe')) {
      return pathName
    }

    return auth.userDetails.subsystems[0].toLowerCase()
    /* const lastLocation = localStorage.getItem(constants.LAST_LOCATION)
    if (lastLocation) {
      return lastLocation
    } else {
      return auth.userDetails.subsystems[0]
    } */
  }

  useEffect(() => {
    /* HANDLE LANGUAGE */
    const lang: string | null = localStorage.getItem(constants.LANG)

    if (lang !== null) {
      i18n.changeLanguage(lang)
    } else {
      localStorage.setItem(constants.LANG, 'hu')
    }

    /* HANDLE TOKEN */
    setDefaultBaseUrl(process.env.NODE_ENV === 'production' ? window.location.origin + '/api' : process.env.REACT_APP_BASE_URL).then(() => {
      const token = localStorage.getItem(constants.TOKEN);
      if (token != null) {
        setAuthToken(token)
        dispatch(getUserDetails());
      } else {
        dispatch(authActions.logout(""));
      }
    })
  }, [dispatch])

  /* CHECK IN */
  useEffect(() => {
    if (auth.isAuthenticated && auth.loading) {
      /* set theme and lang */
      const token: string | null = localStorage.getItem(constants.TOKEN)
      /* const lastLocation: string | null = localStorage.getItem(constants.LAST_LOCATION) */
      const pathName = window.location.pathname
      let module = ''

      if (pathName.includes('/atr')) {
        module = 'atr'
      } else if (pathName.includes('/portal')) {
        module = 'portal'
      } else if (pathName.includes('/efe')) {
        module = 'efe'
      } else {
        /* TODO: YOU MUST INCLUDE ALL MODULE ROUTES HERE */
      }

      if (token) {
        if (module === '') {
          module = auth.userDetails.subsystems[0]
        }

        dispatch(checkIn(module, { session: token }))
      }

    }
  }, [auth, dispatch])

  /* SET PERMISSIONS AFTER CHECK IN, POLL NOTIFICATIONS AND UPDATE REFERENCES IF NEEDED */
  useEffect(() => {
    if (auth.isAuthenticated && !auth.loading) {
      if (auth.userDetails) {
        setPermissions(auth.userDetails.subsystems, auth.rights)

        /* poll notifications */
        dispatch(getNotificationsPoll())
        notificationInterval.current = setInterval(() => {
          dispatch(getNotificationsPoll())
        }, 30000);

        /* set theme and lang */
        const lang: string | null = localStorage.getItem(constants.LANG)
        const theme: string | null = localStorage.getItem(constants.THEME)

        if (lang && theme) {
          if (lang !== auth.userDetails.preferences.language || theme !== auth.userDetails.preferences.theme) {
            dispatch(updatePreferences({ language: lang, theme: theme }))
          }
        }

        if (!auth.activeCompany || isEmpty(auth.activeCompany)) {
          const stored = localStorage.getItem(constants.ACTIVE_COMPANY);

          const activeCompany: Company | null = stored
              ? JSON.parse(stored)
              : null;

          if (!activeCompany) {
            dispatch(getActiveCompany())
          } else {
            dispatch(authActions.setActiveCompany(activeCompany));
          }
        }
      }
    }
  }, [auth, dispatch])

  useEffect(() => {
    if (preferencesResponse) {
      dispatch(clearPreferencesResponse())
    }
  }, [preferencesResponse, dispatch])

  const onFailRequest = (status: number, response: ResponseBase) => {
    if (status) {
      if (status === 403) {
        dispatch(setSnackbarData({
          status: 'danger',
          text: t('noAccessRights')
        }))
      } else if (status === 401 || status === 500) {
        /* logout without using axios call */
        clearInterval(notificationInterval.current)
        notificationInterval.current = null
        localStorage.removeItem(constants.TOKEN);
        setAuthToken("");
        localStorage.removeItem(constants.ACTIVE_COMPANY);
        dispatch(authActions.logout(""));
        if (response) {
          dispatch(authActions.setError(response))
        } else {
          dispatch(authActions.setError({ responseCode: RESPONSE_CODES.INVALID_SESSION }))
        }
      }
    }
  }

  /* HANDLE ERROR */
  setupAxiosInterceptors(onFailRequest)

  /* HANDLE SNACKBAR */
  useEffect(() => {
    if (!isEmpty(snackbar.text)) {
      setShowSnackbar(true)
    }
  }, [snackbar])

  const router = createBrowserRouter(
    !auth.isAuthenticated ? [
      {
        path: "*",
        element: <Navigate to={'/sign-in'} />,
      },
      {
        path: "/sign-in",
        errorElement: <Error404 />,
        element: <SignIn />,
      },
      {
        path: "/setup-password",
        errorElement: <Error404 />,
        element: <PasswordSetup />,
      }
    ] : [
      {
        path: "*",
        element: <Navigate to={getDefaultRoute()} />,
      },
      {
        path: "/setup-password",
        errorElement: <Error404 />,
        element: <PasswordSetup />,
      },
      hasModules(['PORTAL']) ? {
        path: "/portal",
        element: <PortalWrapper />,
        children: [
          {
            path: "/portal/",
            element: <Navigate to={"/portal/dashboard"} />,
          },
          {
            path: "/portal/dashboard",
            element: <PortalDashboard />
          },
          /* hasRights(['tmp_group_management']) ? {
            path: "/portal/groups",
            element: <Groups />
          } : {}, */
          hasRights(['portal_modul_roles_read']) ? {
            path: "/portal/roles",
            element: <Roles />
          } : {},
          hasRights(['portal_modul_users_read']) ? {
            path: "/portal/users",
            element: <PortalUsers />
          } : {},
          {
            path: '/portal/events',
            element: <PortalEvents />
          }
        ]
      } : {},
      hasModules(['EFE']) ? {
        path: "/efe",
        element: <EfeWrapper />,
        children: [
          {
            path: "/efe/",
            element: <Navigate to={"/efe/dashboard"} />,
          },
          {
            path: "/efe/dashboard",
            element: hasRights(['efe_dashboard_view']) ? <EfeDashboard /> : <div />
          },
          hasRights(['efe_pod_list']) ? {
            path: '/efe/pod/measurement-location',
            element: <MeasurementLocation />
          } : {},
          hasRights(['efe_efe_list']) ? {
            path: '/efe/efe/list',
            element: <Device />
          } : {},
          hasRights(['efe_pod_details_view']) ? {
            path: "/efe/pod/details",
            element: <MeasurementLocationDetails />
          } : {},
          hasRights(['efe_efe_param_list']) ? {
            path: "/efe/pod/params",
            element: <EfeParams />
          } : {},
          hasRights(['efe_efe_command_list']) ? {
            path: "/efe/pod/commands",
            element: <DeviceCommands />
          } : {},
          hasRights(['efe_metrics_list']) ? {
            path: "/efe/pod/metrics",
            element: <MetricsScreen />
          } : {},
          hasRights(['efe_mqtt_grab_list']) ? {
            path: "/efe/pod/mqtt-messages",
            element: <MqttMessages />
          } : {},
          hasRights(['efe_measurement_data_list']) ? {
            path: "/efe/measurement-data",
            element: <MeasurementData />
          } : {},
          // hasRights(['efe_location_category_list']) ? {
          //   path: '/efe/pod/measurement-loc-category',
          //   element: <MeasurementLocationCategories />
          // } : {},
          hasRights(['efe_metric_param_view']) ? {
            path: "/efe/control/metric-params",
            element: <MetricParams />
          } : {},
          // hasRights(['efe_registers_view']) ? {
          //   path: "/efe/control/register",
          //   element: <RegisterTable />
          // } : {},
          hasRights(['efe_registers_view']) ? {
            path: "/efe/control/register",
            element: <TestRegisterTable />
          } : {},
          hasRights(['efe_python_script_view']) ? {
            path: "/efe/control/python-script",
            element: <PythonScript />
          } : {},
          // hasRights(['efe_efe_type_list']) ? {
          //   path: "/efe/efe/efe-type",
          //   element: <EfeType />
          // } : {},
          hasRights(['efe_location_list']) ? {
            path: "/efe/pod/locations",
            element: <EfeLocations />
          } : {},
          hasRights(['efe_log_list']) ? {
            path: '/efe/efe/logs',
            element: <EfeLogs />
          } : {},
          hasRights(['efe_modul_users_read', 'efe_provider_users_read']) ? {
            path: "/efe/admin/users",
            element: <EfeUsers />
          } : {},
          hasRights(['efe_modul_provider_read']) ? {
            path: '/efe/admin/providers',
            element: <Providers />
          } : {},
          hasRights(['efe_firmware_list']) ? {
            path: "/efe/efe/firmwares",
            element: <EfeFirmwares />
          } : {},
          // {
          //   path: '/efe/events',
          //   element: <EfeEvents />
          // }
        ]
      } : {},
      hasModules(['ATR']) ? {
        path: "/atr",
        element: <ATRWrapper />,
        children: [
          {
            path: "/atr/",
            element: <Navigate to={"/atr/dashboard"} />,
          },
          {
            path: "/atr/dashboard",
            element: hasRights(['atr_machine_dashboard_view']) ? <ATRDashboard /> : <div />
          },
          hasRights(['atr_machine_list']) ? {
            path: "/atr/machines",
            element: <Machines />
          } : {},
          hasRights(['atr_machine_type_list']) ? {
            path: "/atr/master-data/machine-types",
            element: <MachineTypeScreen />
          } : {},
          hasRights(['atr_machine_details_view']) ? {
            path: "/atr/machines/details",
            element: <MachineDetails />
          } : {},
          hasRights(['atr_afe_param_list']) ? {
            path: "/atr/machines/params",
            element: <OperationalParams />
          } : {},
          hasRights(['atr_afe_command_list']) ? {
            path: "/atr/machines/commands",
            element: <Commands />
          } : {},
          hasRights(['planogram_create']) ? {
            path: "/atr/machines/planogram",
            element: <Planograms />
          } : {},
          hasRights(['atr_machine_log_list']) ? {
            path: "/atr/logs",
            element: <Logs />
          } : {},
          hasRights(['atr_machine_sale_list']) ? {
            path: "/atr/report",
            element: <Report />
          } : {},
          hasRights(['atr_evadts_command_list']) ? {
            path: "/atr/machines/evadts",
            element: <EvaDts />
          } : {},
          hasRights(['atr_modul_users_read', 'atr_company_users_read']) ? {
            path: "/atr/users",
            element: <ATRUsers />
          } : {},
          hasRights(['atr_modul_company_read']) ? {
            path: "/atr/companies",
            element: <Companies />
          } : {},
          hasRights(['atr_machine_category_list']) ? {
            path: "/atr/master-data/machine-categories",
            element: <MachineCategories />
          } : {},
          hasRights(['atr_location_list']) ? {
            path: "/atr/master-data/locations",
            element: <Locations />
          } : {},
          hasRights(['atr_firmware_list']) ? {
            path: "/atr/firmwares",
            element: <Firmwares />
          } : {},
          {
            path: '/atr/events',
            element: <ATREvents />
          }
        ]
      } : {}
    ]
  );

  return (
    <ThemeProvider activeTheme={theme} borderRadius={4} defaultPadding={8} defaultRootSurfaceElevation={3}>
      {auth.loading ? (
        <Loading />
      ) : (
        <RouterProvider router={router} />
      )}

      <Snackbar
        style={{ marginTop: 40 }}
        visible={showSnackbar}
        message={snackbar.text}
        status={snackbar.status}
        closeSnackbar={onDismissSnackBar}
        actionText={'OK'}
      />
    </ThemeProvider>
  );
}

export default App;
